/**
 * Opening sequence - the first thing Megha sees when the page loads.
 *
 * Three layered beats over ~30 seconds:
 *
 *  1. Cold open: black screen + "On April 2, 1998, the sky over Karnataka
 *     looked like this." → overlay fades to reveal her real birth sky.
 *
 *  2. Population: "This is your universe."
 *
 *  3. Invitation: "Twenty-eight years later, you wished your people were
 *     closer." → "They have been here all along." → "Look around. It is
 *     yours." → overlay clears, free movement unlocks.
 *
 * Click anywhere or press ESC at any time to skip to the end.
 *
 * The class manages its own DOM elements, timers, and skip logic. It
 * fires onComplete when finished (either naturally or via skip).
 */
export class Opening {
  constructor({ onComplete }) {
    this.onComplete = onComplete;
    this.isComplete = false;
    this.timers = [];

    this.overlay = document.getElementById('opening-overlay');
    this.text = document.getElementById('opening-text');

    if (!this.overlay || !this.text) {
      console.error('Opening: missing #opening-overlay or #opening-text in DOM');
      return;
    }
  }

  start() {
    if (!this.overlay) {
      this.complete();
      return;
    }

    this.overlay.classList.add('active');
    this.scheduleEvents();

    // Skip on click or ESC
    this._onSkip = () => this.skip();
    this._onKey = (e) => {
      if (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter') this.skip();
    };
    this.overlay.addEventListener('click', this._onSkip);
    document.addEventListener('keydown', this._onKey);
  }

  scheduleEvents() {
    // Helper to schedule
    const at = (ms, fn) => {
      this.timers.push(setTimeout(() => {
        if (!this.isComplete) fn();
      }, ms));
    };

    // Each showText takes ~1s to fade out + 1s to fade in (~2s of pure
    // transition), so we leave at least ~3.5s between calls so each line
    // gets a real visible moment.

    // === Beat 1: cold open ===
    at(500,  () => this.showText('On April 2, 1998, the sky over Karnataka looked like this.'));
    at(4500, () => this.fadeOverlay()); // black background fades to transparent, revealing the sky

    // === Beat 2: population ===
    at(5500,  () => this.showText('This is your universe.'));

    // === Beat 3: invitation ===
    at(9500,  () => this.showText('Twenty-eight years later, you wished your people were closer.'));
    at(13500, () => this.showText('They have been here all along.'));
    at(17500, () => this.showText('Look around. It is yours.'));
    at(21500, () => this.hideText());

    at(23000, () => this.complete());
  }

  showText(text) {
    if (!this.text || this.isComplete) return;
    // Fade out current. Wait for the CSS transition to fully complete
    // (0.9s + 50ms buffer) before swapping content and fading in.
    this.text.classList.remove('visible');
    setTimeout(() => {
      if (this.isComplete) return;
      this.text.textContent = text;
      // Force a reflow so the transition retriggers cleanly
      void this.text.offsetHeight;
      this.text.classList.add('visible');
    }, 950);
  }

  hideText() {
    if (!this.text) return;
    this.text.classList.remove('visible');
  }

  fadeOverlay() {
    if (!this.overlay) return;
    this.overlay.classList.add('cleared');
  }

  skip() {
    if (this.isComplete) return;
    // Cancel all timers, fade everything out fast, fire complete
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.hideText();
    this.fadeOverlay();
    setTimeout(() => this.complete(), 500);
  }

  complete() {
    if (this.isComplete) return;
    this.isComplete = true;
    this.timers.forEach(clearTimeout);
    this.timers = [];
    if (this.overlay) {
      this.overlay.classList.remove('active');
      this.overlay.removeEventListener('click', this._onSkip);
    }
    document.removeEventListener('keydown', this._onKey);
    if (this.onComplete) this.onComplete();
  }
}
